<script lang="ts">
  import { getModalStore } from '@skeletonlabs/skeleton'
  import type { SvelteComponent } from 'svelte'

  import CloseModalButton from '@components/CloseModalButton.svelte'
  import {
    getRestrictedModelConfig,
    isModelSourceApprovedForUnlearning,
  } from '@routes/llm/llms/restricted-models'
  import type { Config } from '@utils/config.svelte'
  import { Product } from '@utils/product'

  interface Props {
    parent: SvelteComponent
  }

  type ModalValue = {
    llmId: number
    llmName: string
    modelSource?: unknown
    config: Config | null
  }

  let { parent }: Props = $props()

  const modalStore = getModalStore()
  const modalValue = $derived(($modalStore[0]?.value ?? {}) as ModalValue)
  const activeProducts = $derived(new Set(modalValue.config?.activeProducts ?? []))
  const canUnlearn = $derived(
    activeProducts.has(Product.UNLEARNING_LLM_BEHAVIOR) ||
      activeProducts.has(Product.UNLEARNING_LLM_DATA),
  )
  const canEval = $derived(activeProducts.has(Product.LLM_BEHAVIOR_EVAL))
  const restrictedModelConfig = $derived(getRestrictedModelConfig(modalValue.config))
  const isApprovedForUnlearning = $derived(
    isModelSourceApprovedForUnlearning(
      modalValue.modelSource,
      restrictedModelConfig.restrictedModelIds,
    ),
  )
  const canRunUnlearning = $derived(canUnlearn && isApprovedForUnlearning)
  const unlearningUnavailableMessage = $derived.by(() => {
    if (!canUnlearn) {
      return 'Behavior unlearning is not enabled for this environment.'
    }
    if (restrictedModelConfig.isRestrictedMode && !isApprovedForUnlearning) {
      return 'Only approved HuggingFace models can run behavior unlearning.'
    }
    return ''
  })
  const showActionChoice = $derived(canUnlearn && canEval)

  const closeModal = (response: 'unlearn' | 'eval' | false = false) => {
    if ($modalStore[0]?.response) {
      $modalStore[0].response(response)
    }
    modalStore.close()
  }
</script>

{#snippet unlearningAction(fullWidth = false)}
  <div class="space-y-1">
    <button
      class="variant-filled-primary btn {fullWidth ? 'w-full' : ''}"
      onclick={() => closeModal('unlearn')}
      disabled={!canRunUnlearning}
    >
      Run Behavior Unlearning
    </button>
    {#if !canRunUnlearning && unlearningUnavailableMessage}
      <p class="text-surface-600-300 text-sm">{unlearningUnavailableMessage}</p>
    {/if}
  </div>
{/snippet}

{#if $modalStore[0]}
  <div class="card w-modal space-y-4 p-4 shadow-xl" role="dialog">
    <h3 class="h3 sticky top-0 justify-between bg-surface-50 p-4 dark:bg-surface-800">
      Choose Action for {modalValue.llmName}
      <CloseModalButton onClose={parent.onClose} />
    </h3>
    <article class="space-y-4 p-4">
      {#if showActionChoice}
        <p>What would you like to run for <strong>{modalValue.llmName}</strong>?</p>
        <div class="space-y-3">
          <button class="variant-filled-secondary btn w-full" onclick={() => closeModal('eval')}>
            Run Evaluation
          </button>
          {@render unlearningAction(true)}
        </div>
      {:else if canRunUnlearning}
        <p>
          Only unlearning is available for <strong>{modalValue.llmName}</strong> in this environment.
        </p>
        {@render unlearningAction()}
      {:else if canUnlearn}
        {@render unlearningAction()}
      {:else if canEval}
        <p>
          Only evaluation is available for <strong>{modalValue.llmName}</strong> in this environment.
        </p>
        <button class="variant-filled-primary btn" onclick={() => closeModal('eval')}>
          Run Evaluation
        </button>
      {:else}
        <p>No run actions are enabled for this environment.</p>
      {/if}
    </article>
    <footer class="modal-footer {parent.regionFooter}">
      <button class="btn {parent.buttonNeutral}" onclick={() => closeModal(false)}>
        {parent.buttonTextCancel}
      </button>
    </footer>
  </div>
{/if}
